import { Link } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { usePageMeta } from "@/hooks/usePageMeta";
import { ArrowRight } from "lucide-react";

interface Section {
  heading: string;
  content: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface RelatedLink {
  name: string;
  href: string;
  description: string;
}

interface GEOArticlePageProps {
  seoTitle: string;
  seoDescription: string;
  headline: string;
  introduction: string;
  sections: Section[];
  faqItems: FAQItem[];
  relatedLinks: RelatedLink[];
  structuredData: Record<string, unknown>[];
}

export default function GEOArticlePage({
  seoTitle,
  seoDescription,
  headline,
  introduction,
  sections,
  faqItems,
  relatedLinks,
  structuredData,
}: GEOArticlePageProps) {
  usePageMeta(seoTitle, seoDescription);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navigation />

      <main className="flex-1">
        {/* Hero */}
        <section className="w-full py-20 px-6 md:px-10 bg-muted">
          <div className="container mx-auto max-w-[50rem] text-center">
            <h1 className="text-4xl md:text-6xl font-medium text-foreground leading-tight">
              {headline}
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-[40rem] mx-auto leading-relaxed">
              {introduction}
            </p>
          </div>
        </section>

        {/* Article Sections */}
        <article className="w-full py-16 px-6 md:px-10">
          <div className="container mx-auto max-w-[50rem] space-y-12">
            {sections.map((section, i) => (
              <section key={i}>
                <h2 className="text-2xl md:text-3xl font-medium text-foreground mb-4">
                  {section.heading}
                </h2>
                <div className="text-muted-foreground leading-relaxed whitespace-pre-line text-base">
                  {section.content}
                </div>
              </section>
            ))}
          </div>
        </article>

        {/* Related Features */}
        <section className="w-full py-16 px-6 md:px-10 bg-muted">
          <div className="container mx-auto max-w-[60rem]">
            <h2 className="text-3xl md:text-4xl font-medium text-foreground text-center mb-12">
              Explore Shattara AI Features
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              {relatedLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="p-6 rounded-xl border border-border bg-background hover:border-primary/40 hover:shadow-md transition-all group"
                >
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    {link.name}
                  </h3>
                  <p className="text-muted-foreground text-sm mt-1">{link.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full py-16 px-6 md:px-10">
          <div className="container mx-auto max-w-[50rem]">
            <h2 className="text-3xl md:text-4xl font-medium text-foreground text-center mb-12">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-foreground">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full py-20 px-6 md:px-10 bg-muted">
          <div className="container mx-auto max-w-[40rem] text-center">
            <h2 className="text-3xl md:text-4xl font-medium text-foreground mb-4">
              Ready to Study Smarter?
            </h2>
            <p className="text-muted-foreground mb-8">
              Join thousands of students using Shattara AI to learn faster and ace their exams.
            </p>
            <Link to="/auth/signup">
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-3 gap-2 group shadow-lg hover:shadow-xl transition-all text-base">
                <span>Try Shattara AI Free</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* Structured Data */}
      {structuredData.map((data, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </div>
  );
}
